/**
 * Breadcrumb collection context: preserves the collection the shopper came from.
 */
(function () {
  const STORAGE_KEY = 'specktrum_breadcrumb_context';
  const STORAGE_DURATION = 30 * 60 * 1000;
  const NAV_FLAG_KEY = 'specktrum_breadcrumb_from_product';
  const EXCLUDED_HANDLES = new Set(['all-products', 'all']);

  function saveCollectionContext(collectionData) {
    if (!collectionData?.handle) return;

    const contextData = {
      handle: collectionData.handle,
      title: collectionData.title,
      url: collectionData.url,
      timestamp: Date.now(),
      expires: Date.now() + STORAGE_DURATION,
    };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(contextData));
    } catch (e) {
      console.warn('Could not save breadcrumb context:', e);
    }
  }

  function getStoredCollectionContext() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const contextData = JSON.parse(stored);

      if (Date.now() > contextData.expires) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return contextData;
    } catch (e) {
      console.warn('Could not retrieve breadcrumb context:', e);
      return null;
    }
  }

  function parseCollectionHandleFromUrl(urlString) {
    if (!urlString) return null;

    try {
      const url = new URL(urlString, window.location.origin);
      const patterns = [
        /\/collections\/([^/?#]+)\/products\//,
        /\/collections\/([^/?#]+)\/?$/,
      ];

      for (const pattern of patterns) {
        const match = url.pathname.match(pattern);
        if (match?.[1] && !EXCLUDED_HANDLES.has(match[1])) {
          return match[1];
        }
      }
    } catch (e) {}

    return null;
  }

  function readProductCollections(breadcrumb) {
    const script = breadcrumb.querySelector('[data-specktrum-product-collections-json]');
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('Could not parse product collections for breadcrumb:', e);
      return [];
    }
  }

  function findCollectionByHandle(handle, productCollections) {
    if (!handle) return null;
    return productCollections.find((entry) => entry.handle === handle) || null;
  }

  function isValidCollectionContext(collectionData, productCollections) {
    if (
      !collectionData?.handle ||
      EXCLUDED_HANDLES.has(collectionData.handle) ||
      !collectionData.title ||
      !collectionData.url
    ) {
      return false;
    }

    if (!productCollections.length) return true;

    return productCollections.some((entry) => entry.handle === collectionData.handle);
  }

  function readLiquidCollection(breadcrumb) {
    const handle = breadcrumb.dataset.currentCollection;
    const title = breadcrumb.dataset.currentCollectionTitle;
    const url = breadcrumb.dataset.currentCollectionUrl;

    if (!handle || !title || !url || EXCLUDED_HANDLES.has(handle)) return null;

    return { handle, title, url };
  }

  function getReferrerCollection(productCollections) {
    if (!document.referrer) return null;

    try {
      const referrer = new URL(document.referrer);
      if (referrer.hostname !== window.location.hostname) return null;
    } catch (e) {
      return null;
    }

    const handle = parseCollectionHandleFromUrl(document.referrer);
    return findCollectionByHandle(handle, productCollections);
  }

  function getCollectionPageContext() {
    const data = document.getElementById('breadcrumb-collection-page-context');
    if (!data?.dataset?.collectionHandle) return null;

    return {
      handle: data.dataset.collectionHandle,
      title: data.dataset.collectionTitle,
      url: data.dataset.collectionUrl,
    };
  }

  function updateBreadcrumbDisplay(breadcrumb, collectionData) {
    const collectionItem = breadcrumb.querySelector('.breadcrumb__collection-item');
    const collectionLink =
      breadcrumb.querySelector('#breadcrumb-collection-link') ||
      (collectionItem ? collectionItem.querySelector('a') : null);

    if (!collectionItem || !collectionLink) return;

    if (!collectionData?.handle) {
      collectionItem.style.display = 'none';
      collectionItem.hidden = true;
      breadcrumb.classList.add('is-ready');
      return;
    }

    collectionLink.href = collectionData.url;
    collectionLink.textContent = collectionData.title;
    collectionItem.style.display = '';
    collectionItem.hidden = false;
    breadcrumb.classList.add('is-ready');
  }

  function resolveCollectionContext(breadcrumb) {
    const productCollections = readProductCollections(breadcrumb);
    const fromProductNav = sessionStorage.getItem(NAV_FLAG_KEY) === '1';

    if (fromProductNav) {
      sessionStorage.removeItem(NAV_FLAG_KEY);
    }

    const storedContext = getStoredCollectionContext();
    const liquidCollection = readLiquidCollection(breadcrumb);
    const referrerCollection = getReferrerCollection(productCollections);

    if (isValidCollectionContext(liquidCollection, productCollections)) {
      return liquidCollection;
    }

    if (isValidCollectionContext(storedContext, productCollections)) {
      return storedContext;
    }

    if (isValidCollectionContext(referrerCollection, productCollections)) {
      return referrerCollection;
    }

    return null;
  }

  function initializeProductBreadcrumb(breadcrumb) {
    const collectionData = resolveCollectionContext(breadcrumb);

    if (collectionData) {
      saveCollectionContext(collectionData);
      updateBreadcrumbDisplay(breadcrumb, collectionData);
      return;
    }

    sessionStorage.removeItem(STORAGE_KEY);
    updateBreadcrumbDisplay(breadcrumb, null);
  }

  function persistContextBeforeProductNavigation(link) {
    const handleFromLink = parseCollectionHandleFromUrl(link.href);
    const pageContext = getCollectionPageContext();
    const breadcrumb = document.querySelector('[data-specktrum-product-breadcrumb]');
    const productCollections = breadcrumb ? readProductCollections(breadcrumb) : [];

    if (handleFromLink) {
      const fromCollectionsMap = findCollectionByHandle(handleFromLink, productCollections);
      if (fromCollectionsMap) {
        saveCollectionContext(fromCollectionsMap);
        try {
          sessionStorage.setItem(NAV_FLAG_KEY, '1');
        } catch (e) {}
        return;
      }

      if (pageContext?.handle === handleFromLink) {
        saveCollectionContext(pageContext);
        try {
          sessionStorage.setItem(NAV_FLAG_KEY, '1');
        } catch (e) {}
        return;
      }
    }

    if (pageContext?.handle) {
      saveCollectionContext(pageContext);
      try {
        sessionStorage.setItem(NAV_FLAG_KEY, '1');
      } catch (e) {}
    }
  }

  function trackProductNavigation(breadcrumb) {
    const productCollections = readProductCollections(breadcrumb);

    function persistContextOnNavigate() {
      const storedContext = getStoredCollectionContext();
      if (isValidCollectionContext(storedContext, productCollections)) {
        try {
          sessionStorage.setItem(NAV_FLAG_KEY, '1');
        } catch (e) {}
        saveCollectionContext(storedContext);
        return;
      }

      const resolved = resolveCollectionContext(breadcrumb);
      if (resolved) {
        try {
          sessionStorage.setItem(NAV_FLAG_KEY, '1');
        } catch (e) {}
        saveCollectionContext(resolved);
      }
    }

    document.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.addEventListener('click', persistContextOnNavigate);
    });

    document.querySelectorAll('.mega-menu__featured-links a').forEach((link) => {
      link.addEventListener('click', persistContextOnNavigate);
    });
  }

  function saveCollectionPageContext() {
    const pageContext = getCollectionPageContext();
    if (!pageContext) return;
    saveCollectionContext(pageContext);
  }

  function bindCollectionToProductNavigation() {
    document.addEventListener(
      'mousedown',
      (event) => {
        const link = event.target.closest('a[href*="/products/"]');
        if (!link) return;
        persistContextBeforeProductNavigation(link);
      },
      true
    );
  }

  window.SpecktrumBreadcrumb = {
    saveCollectionContext,
    getStoredCollectionContext,
    initializeProductBreadcrumb,
    trackProductNavigation,
    saveCollectionPageContext,
    parseCollectionHandleFromUrl,
  };

  function initBreadcrumbNavigation() {
    if (document.documentElement.dataset.specktrumBreadcrumbInit === 'true') {
      return;
    }
    document.documentElement.dataset.specktrumBreadcrumbInit = 'true';

    bindCollectionToProductNavigation();
    saveCollectionPageContext();

    document.querySelectorAll('[data-specktrum-product-breadcrumb]').forEach((breadcrumb) => {
      initializeProductBreadcrumb(breadcrumb);
      trackProductNavigation(breadcrumb);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBreadcrumbNavigation);
  } else {
    initBreadcrumbNavigation();
  }
})();
